import type { MouseEvent } from "react";
import { tg } from "@/lib/telegram";

/**
 * Перехоплює клік по <a> всередині HTML-контейнера (текст вакансії) і
 * відкриває посилання ЗОВНІ — у браузері Telegram або телефона через
 * tg.openLink. Інакше зовнішній сайт вантажиться прямо у webview міні-аппи,
 * він не адаптований, ми ним не керуємо → все ламається (юзер 2026-06-09).
 *
 * t.me / tg:// → openTelegramLink (відкриває нативно в Telegram).
 */
/**
 * Відкрити Telegram-посилання на користувача/чат (t.me/username, t.me/+invite).
 * ЗАВЖДИ через openTelegramLink — це нативний спосіб відкрити чат у Telegram
 * (і на мобільних, і на ПК). Юзер 2026-06-11: openLink на ПК відкривав t.me у
 * Telegram-браузері і повертав в апку — «повний пиздець», треба одразу в
 * Telegram. openTelegramLink відкриває акаунт напряму у клієнті.
 */
export function openTelegramUserLink(url: string): void {
  const webapp = tg();
  // Нормалізуємо t.me-посилання (прибираємо @, пробіли, зайві слеші у
  // username — інакше посилання не резолвиться).
  let clean = (url || "").trim();
  const m = clean.match(/^(https?:\/\/)?(?:t\.me|telegram\.me)\/(.+)$/i);
  if (m) {
    const handle = m[2].replace(/^@+/, "").replace(/\s+/g, "").replace(/^\/+/, "");
    clean = `https://t.me/${handle}`;
  }
  const platform = ((tg() as unknown as { platform?: string })?.platform ?? "").toLowerCase();
  const isMobile = platform === "ios" || platform === "android";

  // Мобільні: openTelegramLink відкриває чат нативно в Telegram.
  if (isMobile && webapp?.openTelegramLink && /^https:\/\/t\.me\//i.test(clean)) {
    webapp.openTelegramLink(clean);
    return;
  }

  // ═══ ДЕСКТОПИ (2026-07-22, розв'язано телеметрією + перебором каналів) ═══
  //
  // Telegram macOS (platform="macos"). Власник клікнув усі 4 канали на ОДНОМУ
  // юзернеймі — ЖОДЕН не дає «одразу порожній діалог» для НЕзнайомого юзера:
  //   openTelegramLink("https://t.me/<u>")        → картка профілю (Send Message);
  //   openTelegramLink("https://t.me/<u>?text=")  → картка профілю;
  //   openTelegramLink("tg://resolve?domain=<u>") → НЕ РОБИТЬ НІЧОГО;
  //   window.open("tg://resolve?domain=<u>")      → відкриває саму Mini App.
  // Висновок: це поведінка САМОГО клієнта — macOS показує картку юзера, якщо
  // діалогу з ним ще не існує (для вже існуючого чату t.me відкриває чат
  // напряму — перевірено на @SergeyDenisyuk). Форсувати порожній діалог з
  // webview неможливо. Тому macOS іде у фолбек openTelegramLink(t.me) нижче:
  // нативно в Telegram, БЕЗ браузера — максимум, що дає клієнт.
  // (window.open(t.me) прибрано раніше — давав ПАРАЗИТНУ вкладку в браузері.)

  // Telegram Desktop (tdesktop) — синтетичний клік по tg://resolve ПРИБРАНО
  // (owner 2026-07-22): у власника він МОВЧКИ не спрацьовував — кнопка просто
  // «нічого не робила», що гірше за будь-який інший результат. Жодних
  // desktop-спецгілок більше немає: ВСІ платформи йдуть єдиним каналом
  // openTelegramLink(t.me) нижче — він завжди дає видимий результат (чат,
  // якщо діалог існує; інакше картка юзера) і ніколи не відкриває браузер.
  // ПРАВИЛО НА МАЙБУТНЄ: не міняти цей канал без ЖИВОЇ перевірки на ТОМУ
  // САМОМУ клієнті (macOS і Windows поводяться по-різному) — цей баг
  // «повертався» саме через фікси наосліп під один клієнт.

  // Фолбек (invite-лінки / нема webapp): openTelegramLink → openLink → browser.
  if (webapp?.openTelegramLink && /^https:\/\/t\.me\//i.test(clean)) {
    webapp.openTelegramLink(clean);
    return;
  }
  if (webapp?.openLink) {
    webapp.openLink(clean);
    return;
  }
  window.open(clean, "_blank", "noopener,noreferrer");
}

/**
 * Відкрити чат/бота в Telegram для КНОПОК ДІЇ (Підтримка @inflow_manager,
 * «Розмістити вакансію» @inflow_finderbot) — owner 2026-07-23.
 *
 * ⚠️ На Telegram macOS `openTelegramLink` МЕРТВИЙ (кнопка «нічого не робить»,
 * власник підтвердив живою перевіркою 2026-07-22, збережено в памʼяті). Працює
 * ТІЛЬКИ `window.open("https://t.me/<u>","_blank")`. Мобільні лишаємо на
 * нативному openTelegramLink. НЕ чіпаємо `openTelegramUserLink` (контакти у
 * стрічці) — щоб не міняти його канал наосліп (той баг «повертався»).
 */
export function openTelegramChat(url: string): void {
  const webapp = tg();
  let clean = (url || "").trim();
  const m = clean.match(/^(https?:\/\/)?(?:t\.me|telegram\.me)\/(.+)$/i);
  if (m) {
    const handle = m[2].replace(/^@+/, "").replace(/\s+/g, "").replace(/^\/+/, "");
    clean = `https://t.me/${handle}`;
  }
  const platform = ((webapp as unknown as { platform?: string })?.platform ?? "").toLowerCase();
  const isMobile = platform === "ios" || platform === "android";
  if (isMobile && webapp?.openTelegramLink && /^https:\/\/t\.me\//i.test(clean)) {
    webapp.openTelegramLink(clean);
    return;
  }
  // Десктоп/веб — window.open (єдине, що працює на macOS).
  window.open(clean, "_blank", "noopener,noreferrer");
}

export function openExternalOnAnchorClick(e: MouseEvent<HTMLElement>): void {
  const anchor = (e.target as HTMLElement).closest("a");
  const href = anchor?.getAttribute("href");
  if (!href) return;
  e.preventDefault();
  e.stopPropagation();

  const webapp = tg();
  // tg:// — лише через openTelegramLink (нема http-аналога).
  if (href.startsWith("tg://")) {
    if (webapp?.openTelegramLink) webapp.openTelegramLink(href);
    else if (webapp?.openLink) webapp.openLink(href);
    return;
  }
  // t.me / telegram.me — мобільні openTelegramLink, ПК/веб openLink.
  if (/^(https?:\/\/)?(t\.me|telegram\.me)\//i.test(href)) {
    const httpsUrl = href.startsWith("http")
      ? href
      : `https://${href.replace(/^\/+/, "")}`;
    openTelegramUserLink(httpsUrl);
    return;
  }
  // Інші посилання — у зовнішній браузер.
  if (webapp?.openLink) {
    webapp.openLink(href);
    return;
  }
  window.open(href, "_blank", "noopener,noreferrer");
}

/**
 * Відкрити відео уроку в НАТИВНОМУ плеєрі (зовні), а не інлайн у webview.
 * Інлайн-webview Telegram не тягне важкі відео (1080p/50fps) — метадата
 * грузиться, але відтворення стопиться на 0 (юзер 2026-06-11). Нативний
 * плеєр телефона/браузера грає їх без проблем.
 *
 * `url` може бути відносним (`/lesson_videos/N.mp4?v=...`) — робимо
 * абсолютним від origin аппки.
 */
export function openVideoExternal(url: string): void {
  const abs = /^https?:\/\//i.test(url)
    ? url
    : (typeof window !== "undefined" ? window.location.origin : "") + url;
  const webapp = tg();
  if (webapp?.openLink) {
    webapp.openLink(abs);
    return;
  }
  window.open(abs, "_blank", "noopener,noreferrer");
}
